/**
 * Community — restyled to redesign_v3 ("Community notices" / "Community
 * desktop" frames). Large title + search, pill sub-tabs (Notices,
 * Discussions, Polls, Events, Issues), each independently wired:
 *   Notices      GET /api/notices              → pinned + all, tap → /notices/<id>
 *   Discussions  GET /api/discussions           → tag chips + cards
 *   Polls        GET /api/polls                 → compact preview, tap → /polls
 *   Events       GET /api/events                → rows, tap → /events/<id>
 *   Issues       GET /api/issues?visibility=community → cards (upvote wired), tap → /issues
 *
 * One single-column layout at every width — inside the desktop sidebar shell it
 * reads like Gate/Vendors (aligned top bar, de-boxed hairline rows). Replaced the
 * old ≥1024px two-pane list+detail (user ask 2026-07-22).
 *
 * NOTE: /api/polls and /api/events return {active,closed} / {upcoming,past}
 * shapes (not the {items:[...]} shape features/community/lib.ts's fetchPolls/
 * fetchEvents expect), so those two are fetched + parsed locally here rather
 * than through the shared fetchers. Issues already matches {items:[...]}, so
 * that one reuses fetchIssues from the shared lib as-is.
 */
import { router } from 'expo-router';
import React, { useState } from 'react';
import { Platform, Pressable, StyleSheet, View, useWindowDimensions } from 'react-native';

import { radius, space } from '@bero/tokens';
import { num, str } from '@/components/gate/api';
import { Icon, type IconName } from '@/features/account-admin/icons';
import {
  ArchAvatar, ArchSectionLabel, CardSkeletons, CatPill, ChipsRow, DateBlock, PillTabs, ScopeBadge, UpvotePill,
  UrgentNoticeCard,
} from '@/features/community/bits';
import {
  fetchIssues, timeAgo, useFetch, withUpvote, upvoteIssue,
  issueCatLabel, discussionTagLabel, type Issue,
} from '@/features/community/lib';
import { useT, monthShort, dayShort, type TFunc } from '@/i18n';
import type { DictKey } from '@/i18n/en';
import { api } from '@/lib/client';
import { BrandBar } from '@/components/shell/BrandBar';
import { useTheme } from '@/theme';
import {
  AppText, Chip, EmptyState, Hairline, IconButton, Pill, Screen, SectionLabel,
} from '@/ui';

const WIDE = 1024;

type Tab = 'notices' | 'discussions' | 'polls' | 'events' | 'issues';
const TABS: Array<readonly [Tab, DictKey]> = [
  ['notices', 'ct_tab_notices'], ['discussions', 'ct_tab_discussions'], ['polls', 'ct_tab_polls'],
  ['events', 'ct_tab_events'], ['issues', 'ct_tab_issues'],
];

const NOTICE_KIND_LABEL: Record<NoticeKind, DictKey> = {
  urgent: 'notice_kind_urgent', event: 'notice_kind_event',
  committee: 'notice_kind_committee', general: 'notice_kind_general',
};

/** Poll scope/deadline are resolved at RENDER (t available) rather than baked
 * into the fetched object, so they localise with the active language. */
function pollScopeLabelT(t: TFunc, voteScope: string): string {
  return !voteScope || voteScope.toUpperCase() === 'ALL' ? t('poll_all_wings') : t('poll_wing', { code: voteScope });
}
function pollDeadlineLabelT(t: TFunc, iso: string, closed: boolean): string {
  if (closed) return t('poll_closed');
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return t('poll_open');
  const days = Math.ceil((d.getTime() - Date.now()) / 86400000);
  if (days <= 0) return t('poll_closes_today');
  if (days === 1) return t('poll_closes_tomorrow');
  return t('poll_closes_in', { n: days });
}

// month/day names via i18n monthShort/dayShort (localised with active language)

/* ---------------- More links (vendors / classifieds / lost & found /
 * celebrations) ----------------
 * These four screens are fully built but previously had zero in-app nav
 * entry point (P0-4) — the design's subTabs mock had a 6th "More" tab that
 * was never implemented. Rather than add a 6th pill tab, this renders as a
 * persistent quick-link row inside the Community hub (visible under every
 * sub-tab), which reaches all four with less chrome than a whole new tab.
 * The sidebar's own Community nav-group entries are a separate batch's
 * territory — not duplicated here. */
const MORE_LINKS: Array<{ icon: IconName; label: DictKey; path: string }> = [
  { icon: 'wrench', label: 'ml_vendors', path: '/vendors' },
  { icon: 'package', label: 'ml_classifieds', path: '/classifieds' },
  { icon: 'search', label: 'ml_lost_found', path: '/lost-found' },
  { icon: 'calendar', label: 'ml_celebrations', path: '/birthdays' },
];

function MoreLinksRow({ inset = space.s4 }: { inset?: number }) {
  const { colors } = useTheme();
  const { t } = useT();
  return (
    <View style={{ marginHorizontal: -inset, marginBottom: space.s2 }}>
      <ChipsRow>
        {MORE_LINKS.map((l) => (
          <Pressable
            key={l.path}
            accessibilityRole="button"
            accessibilityLabel={t(l.label)}
            onPress={() => router.push(l.path)}
            style={{
              flexDirection: 'row', alignItems: 'center', gap: 6,
              paddingHorizontal: 12, paddingVertical: 8, borderRadius: radius.full,
              borderWidth: StyleSheet.hairlineWidth, borderColor: colors.borderSoft, backgroundColor: colors.canvas0,
            }}
          >
            <Icon name={l.icon} size={14} color={colors.textSecondary} />
            <AppText variant="bodySm" weight="500">{t(l.label)}</AppText>
          </Pressable>
        ))}
      </ChipsRow>
    </View>
  );
}

function arr(body: unknown, key: string): unknown[] {
  const v = (body && typeof body === 'object') ? (body as Record<string, unknown>)[key] : undefined;
  return Array.isArray(v) ? v : [];
}

/* ---------------- notices ---------------- */
type NoticeKind = 'urgent' | 'event' | 'committee' | 'general';
const NOTICE_KINDS: NoticeKind[] = ['urgent', 'event', 'committee', 'general'];
type CNotice = { id: string; kind: NoticeKind; title: string; body: string; when: string; pinned: boolean };

function toCNotice(raw: unknown): CNotice | null {
  const id = str(raw, 'id');
  const title = str(raw, 'title');
  if (!id || !title) return null;
  const kindRaw = str(raw, 'kind') ?? 'general';
  const kind = (NOTICE_KINDS as string[]).includes(kindRaw) ? (kindRaw as NoticeKind) : 'general';
  const createdAt = str(raw, 'createdAt');
  const d = createdAt ? new Date(createdAt) : null;
  return {
    id, kind, title, body: str(raw, 'body') ?? '',
    when: d && !Number.isNaN(d.getTime()) ? timeAgo(d) : '',
    pinned: !!(raw && typeof raw === 'object' && (raw as Record<string, unknown>).pinned === true),
  };
}

async function fetchCNotices(): Promise<CNotice[]> {
  const body = await api.get<unknown>('/api/notices?per=50');
  return arr(body, 'items').map(toCNotice).filter((n): n is CNotice => n !== null);
}

/* ---------------- discussions ---------------- */
type CDiscussion = { id: string; author: string; flat: string; title: string; body: string; when: string; tag: string; replies: number; views: number };

function toCDiscussion(raw: unknown): CDiscussion | null {
  const id = str(raw, 'id');
  const title = str(raw, 'title');
  if (!id || !title) return null;
  const createdAt = str(raw, 'createdAt');
  const d = createdAt ? new Date(createdAt) : null;
  return {
    id,
    author: str(raw, 'author') ?? 'Resident',
    flat: str(raw, 'flat') ?? '',
    title,
    body: str(raw, 'body') ?? '',
    when: d && !Number.isNaN(d.getTime()) ? timeAgo(d) : '',
    tag: str(raw, 'tag') ?? 'General',
    replies: num(raw, 'replyCount', 'replies') ?? 0,
    views: num(raw, 'views') ?? 0,
  };
}

async function fetchCDiscussions(): Promise<CDiscussion[]> {
  const body = await api.get<unknown>('/api/discussions?per=50');
  return arr(body, 'items').map(toCDiscussion).filter((d): d is CDiscussion => d !== null);
}

/* ---------------- polls (compact) ---------------- */
// voteScope + deadline are kept RAW and localised at render (pollScopeLabelT /
// pollDeadlineLabelT above), so the labels follow the active language.
type CPoll = { id: string; question: string; voteScope: string; anonymous: boolean; status: 'active' | 'closed'; totalVotes: number; eligibleVoters: number; deadline: string };

function toCPoll(raw: unknown, closed: boolean): CPoll | null {
  const id = str(raw, 'id');
  const question = str(raw, 'question');
  if (!id || !question) return null;
  return {
    id, question,
    voteScope: str(raw, 'voteScope') ?? 'ALL',
    anonymous: !!(raw && typeof raw === 'object' && (raw as Record<string, unknown>).isAnonymous === true),
    status: closed ? 'closed' : 'active',
    totalVotes: num(raw, 'totalVotes') ?? 0,
    eligibleVoters: num(raw, 'eligibleVoters') ?? 0,
    deadline: str(raw, 'deadline') ?? '',
  };
}

async function fetchCPolls(): Promise<CPoll[]> {
  const body = await api.get<unknown>('/api/polls');
  const active = arr(body, 'active').map((p) => toCPoll(p, false));
  const closed = arr(body, 'closed').map((p) => toCPoll(p, true));
  return [...active, ...closed].filter((p): p is CPoll => p !== null);
}

/* ---------------- events ---------------- */
type CEvent = { id: string; title: string; venue: string; category: string; day: string; month: string; when: string; past: boolean };

function formatEventWhen(d: Date): string {
  let h = d.getHours();
  const am = h < 12 ? 'AM' : 'PM';
  h = h % 12 || 12;
  const mm = String(d.getMinutes()).padStart(2, '0');
  return `${dayShort(d.getDay())}, ${d.getDate()} ${monthShort(d.getMonth())} · ${h}:${mm} ${am}`;
}

function toCEvent(raw: unknown, past: boolean): CEvent | null {
  const id = str(raw, 'id');
  const title = str(raw, 'title');
  if (!id || !title) return null;
  const dateRaw = str(raw, 'eventDate');
  const d = dateRaw ? new Date(dateRaw) : null;
  const valid = d && !Number.isNaN(d.getTime());
  return {
    id, title,
    venue: str(raw, 'venue') ?? '',
    category: str(raw, 'category') ?? 'General',
    day: valid ? String(d.getDate()) : '·',
    month: valid ? monthShort(d.getMonth()) : '',
    when: valid ? formatEventWhen(d) : '',
    past,
  };
}

async function fetchCEvents(): Promise<CEvent[]> {
  const body = await api.get<unknown>('/api/events');
  const upcoming = arr(body, 'upcoming').map((e) => toCEvent(e, false));
  const past = arr(body, 'past').map((e) => toCEvent(e, true));
  return [...upcoming, ...past].filter((e): e is CEvent => e !== null);
}

/* ---------------- screen ---------------- */

export default function CommunityTab() {
  const { colors } = useTheme();
  const { t } = useT();
  const { width } = useWindowDimensions();
  const wide = Platform.OS === 'web' && width >= WIDE;
  const [tab, setTab] = useState<Tab>('notices');

  const tTabs = TABS.map(([k, key]) => [k, t(key)] as const);

  // One consistent single-column layout on every width — inside the desktop
  // sidebar shell it reads like Gate/Vendors (aligned top, de-boxed content),
  // instead of the old bespoke two-pane. Desktop drops the big hero heading (the
  // BrandBar already labels the page, like Gate) and the More-links chips (the
  // sidebar's Community group covers them).
  return (
    <Screen scroll={false} safeBottom={false} style={{ flex: 1 }}>
      <BrandBar title={t('tab_community')} />
      {!wide ? (
        <View style={{
          flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between',
          paddingHorizontal: space.s4, paddingTop: space.s2, paddingBottom: space.s2,
        }}>
          <AppText variant="displayLg" display>{t('tab_community')}</AppText>
          <IconButton label={t('search')} onPress={() => router.push('/account/search')}>
            <Icon name="search" size={20} color={colors.textPrimary} />
          </IconButton>
        </View>
      ) : null}
      <PillTabs tabs={tTabs} value={tab} onChange={setTab} />
      <Screen padded style={{ paddingTop: space.s4 }}>
        {!wide ? <MoreLinksRow /> : null}
        {tab === 'notices' ? <NoticesTab /> : null}
        {tab === 'discussions' ? <DiscussionsTab /> : null}
        {tab === 'polls' ? <PollsTab /> : null}
        {tab === 'events' ? <EventsTab /> : null}
        {tab === 'issues' ? <IssuesTab /> : null}
      </Screen>
    </Screen>
  );
}

/* ---------------- Notices sub-tab (mobile) ---------------- */
function NoticesTab() {
  const { colors } = useTheme();
  const { t } = useT();
  const { data, loading, error, refetch } = useFetch(fetchCNotices, []);
  const notices = data ?? [];
  const pinned = notices.filter((n) => n.pinned);
  const rest = notices.filter((n) => !n.pinned);
  // Amber is the screen's one accent moment — only the very first urgent
  // notice gets the amber hero treatment; any further urgent notices fall
  // back to the plain NoticeCard styling.
  const amberId = (pinned.find((n) => n.kind === 'urgent') ?? rest.find((n) => n.kind === 'urgent'))?.id ?? null;

  if (loading) return <CardSkeletons count={4} />;
  if (error) {
    return (
      <EmptyState title={t('ct_notices_err_title')} body={t('ct_check_connection')} cta={t('retry')} onCta={() => void refetch()} />
    );
  }
  if (notices.length === 0) {
    return <EmptyState title={t('ct_notices_empty_title')} body={t('ct_notices_empty_body')} />;
  }
  const NoticeRow = ({ n }: { n: CNotice }) =>
    n.id === amberId ? (
      <UrgentNoticeCard title={n.title} when={n.when} onPress={() => router.push(`/notices/${n.id}`)} />
    ) : (
      <Pressable
        accessibilityRole="button"
        onPress={() => router.push(`/notices/${n.id}`)}
        style={({ pressed }) => ({ paddingVertical: space.s4, borderRadius: radius.md, backgroundColor: pressed ? colors.canvas1 : 'transparent' })}
      >
        <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
          <CatPill label={t(NOTICE_KIND_LABEL[n.kind])} />
          <AppText variant="bodySm" tone="tertiary" style={{ marginLeft: 'auto' }}>{n.when}</AppText>
        </View>
        <AppText variant="headingSm" weight="500" style={{ marginTop: space.s2 }} numberOfLines={2}>{n.title}</AppText>
        {n.body ? <AppText variant="bodyMd" tone="secondary" numberOfLines={2} style={{ marginTop: 4 }}>{n.body}</AppText> : null}
      </Pressable>
    );
  const list = (arr: CNotice[]) => arr.map((n, i) => (
    <View key={n.id}>
      <NoticeRow n={n} />
      {i < arr.length - 1 ? <Hairline /> : null}
    </View>
  ));
  return (
    <View>
      {pinned.length > 0 ? (
        <>
          <View style={{ flexDirection: 'row', alignItems: 'center', gap: 6, marginBottom: 2 }}>
            <Icon name="pin" size={12} color={colors.textTertiary} />
            <AppText variant="label" tone="tertiary">{t('ct_pinned')}</AppText>
          </View>
          {list(pinned)}
          <SectionLabel style={{ marginTop: space.s3 }}>{t('ct_all_notices')}</SectionLabel>
        </>
      ) : null}
      {list(rest)}
    </View>
  );
}

/* ---------------- Discussions sub-tab ---------------- */
function DiscussionsTab() {
  const { t } = useT();
  const { data, loading, error, refetch } = useFetch(fetchCDiscussions, []);
  const [tag, setTag] = useState('All');
  const discussions = data ?? [];
  const tags = ['All', ...Array.from(new Set(discussions.map((d) => d.tag)))];
  const filtered = tag === 'All' ? discussions : discussions.filter((d) => d.tag === tag);

  if (loading) return <CardSkeletons count={3} />;
  if (error) {
    return (
      <EmptyState title={t('ct_disc_err_title')} body={t('ct_check_connection')} cta={t('retry')} onCta={() => void refetch()} />
    );
  }
  if (discussions.length === 0) {
    return <EmptyState title={t('ct_disc_empty_title')} body={t('ct_disc_empty_body')} />;
  }
  return (
    <View>
      <View style={{ marginHorizontal: -space.s4, marginBottom: space.s2 }}>
        <ChipsRow>
          {tags.map((tg) => <Chip key={tg} label={tg === 'All' ? t('filter_all') : discussionTagLabel(tg, t)} active={tag === tg} onPress={() => setTag(tg)} />)}
        </ChipsRow>
      </View>
      <View>
        {filtered.map((d, i) => (
          <View key={d.id}>
            <View style={{ paddingVertical: space.s4 }}>
              <View style={{ flexDirection: 'row', alignItems: 'center', gap: space.s3 }}>
                <ArchAvatar name={d.author} size={32} />
                <View style={{ flex: 1, minWidth: 0 }}>
                  <AppText variant="bodyMd" weight="500" numberOfLines={1}>
                    {d.author}{d.flat ? <AppText variant="bodyMd" tone="tertiary"> · {d.flat}</AppText> : null}
                  </AppText>
                </View>
                <AppText variant="bodySm" tone="tertiary">{d.when}</AppText>
              </View>
              <AppText variant="headingSm" weight="500" style={{ marginTop: space.s3 }}>{d.title}</AppText>
              {d.body ? <AppText variant="bodyMd" tone="secondary" numberOfLines={2} style={{ marginTop: 4 }}>{d.body}</AppText> : null}
              <AppText variant="bodySm" tone="tertiary" style={{ marginTop: space.s3 }}>
                {d.replies === 1 ? t('ct_reply') : t('ct_replies', { n: d.replies })} · {t('ct_views', { n: d.views })}
              </AppText>
            </View>
            {i < filtered.length - 1 ? <Hairline /> : null}
          </View>
        ))}
        {filtered.length === 0 ? <EmptyState title={t('ct_nothing_here')} body={t('ct_disc_none_body')} /> : null}
      </View>
    </View>
  );
}

/* ---------------- Polls sub-tab (compact) ---------------- */
function PollsTab() {
  const { colors } = useTheme();
  const { t } = useT();
  const { data, loading, error, refetch } = useFetch(fetchCPolls, []);
  const polls = (data ?? []).filter((p) => p.status === 'active');

  if (loading) return <CardSkeletons count={2} />;
  if (error) {
    return <EmptyState title={t('ct_polls_err_title')} body={t('ct_check_connection')} cta={t('retry')} onCta={() => void refetch()} />;
  }
  return (
    <View>
      <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', marginBottom: space.s3 }}>
        <ArchSectionLabel style={{ marginBottom: 0 }}>{t('ct_active_polls')}</ArchSectionLabel>
        <Pressable accessibilityRole="link" onPress={() => router.push('/polls')} hitSlop={8}>
          <AppText variant="bodySm" weight="500" tone="brand">{t('ct_see_all')}</AppText>
        </Pressable>
      </View>
      {polls.length === 0 ? (
        <EmptyState title={t('ct_polls_empty_title')} body={t('ct_polls_empty_body')} />
      ) : (
        <View>
          {polls.map((p, i) => (
            <View key={p.id}>
              <Pressable
                accessibilityRole="button"
                onPress={() => router.push('/polls')}
                style={({ pressed }) => ({ paddingVertical: space.s4, borderRadius: radius.md, backgroundColor: pressed ? colors.canvas1 : 'transparent' })}
              >
                <View style={{ flexDirection: 'row', alignItems: 'center', gap: space.s2 }}>
                  <ScopeBadge scope={pollScopeLabelT(t, p.voteScope)} />
                  {p.anonymous ? <Pill status="pending" label={t('ct_anonymous')} /> : null}
                </View>
                <AppText variant="headingSm" weight="500" style={{ marginTop: 10 }}>{p.question}</AppText>
                <View style={{ flexDirection: 'row', justifyContent: 'space-between', marginTop: 10 }}>
                  <AppText variant="bodySm" tone="tertiary">{t('ct_voted', { n: p.totalVotes, total: p.eligibleVoters })}</AppText>
                  <AppText variant="bodySm" weight="500" tone="brand">{pollDeadlineLabelT(t, p.deadline, p.status === 'closed')}</AppText>
                </View>
              </Pressable>
              {i < polls.length - 1 ? <Hairline /> : null}
            </View>
          ))}
        </View>
      )}
    </View>
  );
}

/* ---------------- Events sub-tab ---------------- */
function EventsTab() {
  const { t } = useT();
  const { data, loading, error, refetch } = useFetch(fetchCEvents, []);
  const events = data ?? [];
  const upcoming = events.filter((e) => !e.past);
  const past = events.filter((e) => e.past);

  if (loading) return <CardSkeletons count={3} />;
  if (error) {
    return <EmptyState title={t('ct_events_err_title')} body={t('ct_check_connection')} cta={t('retry')} onCta={() => void refetch()} />;
  }
  if (events.length === 0) {
    return <EmptyState title={t('ct_events_empty_title')} body={t('ct_events_empty_body')} />;
  }
  return (
    <View style={{ gap: space.s6 }}>
      <View>
        <ArchSectionLabel>{t('ct_upcoming')}</ArchSectionLabel>
        {upcoming.length === 0 ? (
          <AppText variant="bodySm" tone="tertiary">{t('ct_no_upcoming')}</AppText>
        ) : (
          <View>
            {upcoming.map((e, i) => (
              <View key={e.id}>
                <EventRow e={e} />
                {i < upcoming.length - 1 ? <Hairline /> : null}
              </View>
            ))}
          </View>
        )}
      </View>
      {past.length > 0 ? (
        <View>
          <ArchSectionLabel>{t('ct_past')}</ArchSectionLabel>
          <View>
            {past.map((e, i) => (
              <View key={e.id}>
                <EventRow e={e} />
                {i < past.length - 1 ? <Hairline /> : null}
              </View>
            ))}
          </View>
        </View>
      ) : null}
    </View>
  );
}

function EventRow({ e }: { e: CEvent }) {
  const { colors } = useTheme();
  return (
    <Pressable
      accessibilityRole="button"
      onPress={() => router.push(`/events/${e.id}`)}
      style={({ pressed }) => ({ paddingVertical: space.s4, borderRadius: radius.md, opacity: e.past ? 0.6 : 1, backgroundColor: pressed ? colors.canvas1 : 'transparent' })}
    >
      <View style={{ flexDirection: 'row', gap: space.s3, alignItems: 'center' }}>
        <DateBlock day={e.day} month={e.month} />
        <View style={{ flex: 1, minWidth: 0 }}>
          <AppText variant="bodyMd" weight="500" numberOfLines={1}>{e.title}</AppText>
          <AppText variant="bodySm" tone="tertiary" style={{ marginTop: 2 }} numberOfLines={1}>{e.when}</AppText>
          <AppText variant="bodySm" tone="tertiary" style={{ marginTop: 2 }} numberOfLines={1}>{e.venue}</AppText>
        </View>
      </View>
    </Pressable>
  );
}

/* ---------------- Issues sub-tab ---------------- */
function IssuesTab() {
  const { colors } = useTheme();
  const { t } = useT();
  const { data, loading, error, refetch, mutate } = useFetch(() => fetchIssues('COMMUNITY'), []);
  const issues = data ?? [];

  const toggleUpvote = async (issue: Issue) => {
    const prev = data;
    mutate((list) => (list ?? []).map((i) => (i.id === issue.id ? withUpvote(i) : i)));
    try {
      await upvoteIssue(issue.id);
    } catch {
      mutate(() => prev);
    }
  };

  if (loading) return <CardSkeletons count={3} />;
  if (error) {
    return <EmptyState title={t('ct_issues_err_title')} body={t('ct_check_connection')} cta={t('retry')} onCta={() => void refetch()} />;
  }
  return (
    <View>
      <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', marginBottom: space.s3 }}>
        <ArchSectionLabel style={{ marginBottom: 0 }}>{t('ct_community_issues')}</ArchSectionLabel>
        <Pressable accessibilityRole="link" onPress={() => router.push('/issues')} hitSlop={8}>
          <AppText variant="bodySm" weight="500" tone="brand">{t('ct_see_all')}</AppText>
        </Pressable>
      </View>
      {issues.length === 0 ? (
        <EmptyState title={t('ct_issues_empty_title')} body={t('ct_issues_empty_body')} />
      ) : (
        <View>
          {issues.map((it, i) => (
            <View key={it.id}>
              <Pressable
                accessibilityRole="button"
                onPress={() => router.push('/issues')}
                style={({ pressed }) => ({ paddingVertical: space.s4, borderRadius: radius.md, backgroundColor: pressed ? colors.canvas1 : 'transparent' })}
              >
                <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' }}>
                  <CatPill label={issueCatLabel(it.category, t)} />
                  <Pill status={it.status} />
                </View>
                <AppText variant="headingSm" weight="500" style={{ marginTop: 8 }}>{it.title}</AppText>
                <View style={{ flexDirection: 'row', alignItems: 'center', gap: space.s4, marginTop: 10 }}>
                  <UpvotePill count={it.upvotes} active={it.upvoted} onPress={() => void toggleUpvote(it)} />
                  <AppText variant="bodySm" tone="tertiary">{t('ct_comments', { n: it.comments })}</AppText>
                </View>
              </Pressable>
              {i < issues.length - 1 ? <Hairline /> : null}
            </View>
          ))}
        </View>
      )}
    </View>
  );
}
